/**
 * Mollie-Method-Helpers für die Admin-UI.
 *
 * - `MOLLIE_METHODS`: Whitelist der Methoden, für die wir lokale Fallback-Icons
 *   und i18n-Labels pflegen (Quelle: Phase 9-UI-Initiative, verbindliche
 *   Design-Entscheidung "Variante B").
 * - `mollieMethodLabel`: Liefert die lokalisierte Beschriftung einer Methode
 *   (Fallback: roher Method-Code, z.B. "ideal").
 * - `mollieCdnIconUrl`: Liefert die offizielle Mollie-CDN-Icon-URL — wird als
 *   Primary-Bildquelle im `PaymentMethodBadge` genutzt, mit lokalen SVGs als
 *   `onError`-Fallback für Offline-Render (E-Mail/PDF).
 *
 * Node 14 / TypeScript 4.7 kompatibel — keine Nullish-Coalescing-Assignments,
 * kein `String.replaceAll`, kein `Object.hasOwn`.
 */
import i18n from "../services/i18n";

export const MOLLIE_METHODS = [
    "creditcard",
    "paypal",
    "directdebit",
    "banktransfer",
    "applepay",
    "googlepay",
    "wero",
] as const;

export type MollieMethod = typeof MOLLIE_METHODS[number];

/**
 * Liefert das lokalisierte Label für eine Mollie-Methode.
 *
 * Nur Methoden aus `MOLLIE_METHODS` haben registrierte i18n-Keys. Für alles
 * andere (unbekannte Codes, neu von Mollie hinzugefügte Methoden) geben wir
 * den Roh-Code zurück, damit kein i18n-Identity-String ("i18n.views...") im
 * UI sichtbar wird.
 */
export function mollieMethodLabel(method: string): string {
    const isKnown = (MOLLIE_METHODS as readonly string[]).indexOf(method) !== -1;

    if (!isKnown) {
        return method;
    }

    // Der i18n-Proxy setzt bei registrierten Keys den realen String zurück;
    // bei fehlender Translation liefert er einen "i18n..."-Identity-String.
    // In letzterem Fall fallen wir bewusst auf den Roh-Code zurück, damit
    // kein Debug-Leak ins UI kommt (z.B. bei fehlgeschlagenem Translation-Load).
    const labels = i18n.views.PaymentMethodBadge.mollieMethods as
        Record<string, () => string>;
    const translated = labels[method]();

    if (translated.indexOf("i18n.") === 0) {
        return method;
    }

    return translated;
}

/**
 * Liefert die offizielle Mollie-CDN-URL für ein Methoden-Icon.
 * Mollie stellt diese URLs in der Payment-Methods-API als `image.size1x`
 * zur Verfügung und garantiert sie als stabile CDN-Endpoints.
 */
export function mollieCdnIconUrl(method: string): string {
    return `https://www.mollie.com/external/icons/payment-methods/${method}.svg`;
}

/**
 * Mollie-Awareness-Helper für Buchungs-Render-Stellen.
 *
 * Admin-UI-Stellen, die eine bestehende Buchung anzeigen (z.B. BookingEdit-
 * Step-Summary), müssen unterscheiden, ob die Buchung bereits via Mollie
 * abgewickelt wurde — die Booker-Default-PaymentMethod (oft "Bar" oder
 * SEPA) sagt nichts über die tatsächliche Zahlungsabwicklung aus.
 *
 * Regel:
 * - Keine `scheduledPayments` oder kein Mollie-Eintrag → null
 *   (Aufrufer rendert den Default-Badge).
 * - Mindestens ein Mollie-SP vorhanden → bevorzuge bereits bezahlte
 *   (`paymentDone`), sonst den jüngsten (`createDate`, mit Fallback auf
 *   `paymentDue`/Array-Reihenfolge für Queries ohne `createDate`).
 *
 * Das Generic akzeptiert beliebige SP-Shapes, solange die Mollie-Felder
 * drin sind — so können Konsumenten trotz unterschiedlicher GraphQL-
 * Selection-Sets den Helper verwenden.
 */
export function findDominantMollieSp<
    T extends {
        paymentProvider?: string | null;
        mollieMethod?: string | null;
        createDate?: string | null;
        paymentDone?: string | null;
        paymentDue?: string | null;
    }
>(sps: Array<T> | null | undefined): T | null {
    if (!sps || sps.length === 0) {
        return null;
    }

    const mollieSps = sps.filter(
        (sp) => sp.paymentProvider === "Mollie" && Boolean(sp.mollieMethod),
    );

    if (mollieSps.length === 0) {
        return null;
    }

    const paid = mollieSps.filter((sp) => Boolean(sp.paymentDone));

    if (paid.length > 0) {
        // Jüngste bezahlte Rate bevorzugen (localeCompare-Sortierung
        // auf ISO-Datumsstrings ist stabil auch ohne `createDate`).
        return paid
            .slice()
            .sort((a, b) => (b.paymentDone || "").localeCompare(a.paymentDone || ""))[0];
    }

    // Kein paymentDone — jüngste per createDate, sonst paymentDue,
    // sonst letzter Eintrag (Array-Reihenfolge).
    return mollieSps
        .slice()
        .sort((a, b) => {
            const bKey = b.createDate || b.paymentDue || "";
            const aKey = a.createDate || a.paymentDue || "";

            return bKey.localeCompare(aKey);
        })[0];
}
